import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, getErrorMessage, requestJson, requestJsonOrThrow } from "./http";
import type { AccessibleAppSummary, User } from "./types";

interface AuthResult {
  ok: boolean;
  error?: string;
}

interface AuthAppsResult {
  apps: AccessibleAppSummary[];
  defaultAppPath: string | null;
}

export function useAuthState() {
  const queryClient = useQueryClient();
  const [authError, setAuthError] = useState<string | null>(null);

  const currentUserQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async (): Promise<User | null> => {
      const result = await requestJson<{ user: User }>("/api/auth/me");
      if (result.status === 401) return null;
      if (!result.ok || !("user" in result.data)) {
        throw new ApiError(result.status, "Failed to load current user", result.data);
      }
      return result.data.user;
    },
    staleTime: 30_000,
  });

  const authAppsQuery = useQuery({
    queryKey: ["auth", "apps"],
    enabled: Boolean(currentUserQuery.data),
    queryFn: async (): Promise<AuthAppsResult> => {
      const result = await requestJson<AuthAppsResult>("/api/auth/apps");
      if (result.status === 401) {
        queryClient.setQueryData(["auth", "me"], null);
        return { apps: [], defaultAppPath: null };
      }
      if (!result.ok || !("apps" in result.data)) {
        throw new ApiError(result.status, "Failed to load app access", result.data);
      }
      return result.data;
    },
    staleTime: 30_000,
  });

  const loginMutation = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      return requestJsonOrThrow<{ user: User }>("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
    },
    onSuccess(data) {
      setAuthError(null);
      queryClient.setQueryData(["auth", "me"], data.user);
      void queryClient.invalidateQueries({ queryKey: ["auth", "apps"] });
    },
    onError(error) {
      setAuthError(getErrorMessage(error, "Authentication failed"));
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => requestJsonOrThrow<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
    onSettled() {
      queryClient.setQueryData(["auth", "me"], null);
      queryClient.setQueryData(["auth", "apps"], { apps: [], defaultAppPath: null } satisfies AuthAppsResult);
    },
  });

  const login = async (username: string, password: string): Promise<AuthResult> => {
    try {
      await loginMutation.mutateAsync({ username, password });
      return { ok: true };
    } catch (error) {
      const message = getErrorMessage(error, "Authentication failed");
      return { ok: false, error: message };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      queryClient.setQueryData(["auth", "me"], null);
    }
  };

  const clearAuth = () => {
    queryClient.setQueryData(["auth", "me"], null);
    queryClient.setQueryData(["auth", "apps"], { apps: [], defaultAppPath: null } satisfies AuthAppsResult);
    setAuthError(null);
  };

  const refreshCurrentUser = async () => {
    await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  };

  return {
    user: currentUserQuery.data ?? null,
    isLoadingUser: currentUserQuery.isPending,
    isLoadingApps: Boolean(currentUserQuery.data) && authAppsQuery.isPending,
    isAuthSubmitting: loginMutation.isPending,
    authError,
    appsError: authAppsQuery.error ? getErrorMessage(authAppsQuery.error, "Failed to load app access") : null,
    accessibleApps: authAppsQuery.data?.apps ?? [],
    defaultAppPath: authAppsQuery.data?.defaultAppPath ?? null,
    login,
    logout,
    clearAuth,
    refreshCurrentUser,
  };
}
